/**
 * Notification slide-over panel for POS.
 *
 * Uses the Drawer component from @bbpos/ui for slide-in behavior.
 * Date-grouped notification cards with type icons, priority indicators.
 * Mobile: full-screen overlay.
 *
 * PAGES.md §3.72, STATE_MANAGEMENT.md §2.7
 * Traceability: FR-NOTIF-001 to FR-NOTIF-010
 */

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer, Skeleton, EmptyState } from '@bbpos/ui';
import { useNotificationStore } from '@/stores/use-notification-store';
import type { Notification } from '@/services/notification.service';
import { groupNotifications, notificationHref } from '@/lib/notification-constants';
import { SwipeableNotificationCard } from '@/components/common/SwipeableNotificationCard';
import { GLASS_FORM, GLASS_FILTER, cn } from '@/constants/design-tokens';

// ─── Icons ──────────────────────────────────────────────────────────────────

function CheckAllIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function BellEmptyIcon() {
  return (
    <svg className="h-14 w-14 text-brand-300 dark:text-brand-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

// NotificationCardBody now lives in ./NotificationCardBody.tsx so the
// SwipeableNotificationCard wrapper can import it without a circular
// dependency through this file.

// ─── Component ──────────────────────────────────────────────────────────────

export function NotificationPanel() {
  const navigate = useNavigate();
  const isOpen = useNotificationStore((s) => s.isDropdownOpen);
  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const isLoading = useNotificationStore((s) => s.isLoading);
  const hasMore = useNotificationStore((s) => s.hasMore);
  const closeDropdown = useNotificationStore((s) => s.closeDropdown);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const loadMore = useNotificationStore((s) => s.loadMore);

  // Grouped notifications
  const grouped = useMemo(() => groupNotifications(notifications), [notifications]);

  // T24: open the record's POS screen, else the notification's detail view.
  const handleClick = useCallback(
    async (n: Notification) => {
      if (!n.is_read) {
        await markAsRead(n.id);
      }
      closeDropdown();
      navigate(notificationHref(n));
    },
    [markAsRead, closeDropdown, navigate],
  );

  const handleViewAll = useCallback(() => {
    closeDropdown();
    navigate('/pos/notifications');
  }, [closeDropdown, navigate]);

  return (
    <Drawer
      open={isOpen}
      onClose={closeDropdown}
      position="right"
      width="380px"
      title="Notifications"
    >
      <div className="flex flex-col h-full">
        {/* Sub-header: unread count + mark all — GLASS_FILTER pill */}
        <div className="shrink-0 px-4 pt-3 pb-2">
          <div className={cn(GLASS_FILTER, 'flex items-center justify-between px-3 py-2')}>
            <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </span>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllAsRead()}
                className="text-brand-600 dark:text-brand-400"
              >
                <CheckAllIcon />
                <span className="ml-1.5">Mark All Read</span>
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {isLoading && notifications.length === 0 ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={cn(GLASS_FORM, 'p-3.5 flex gap-3')}>
                  <Skeleton variant="circle" width={36} height={36} />
                  <div className="flex-1 space-y-2">
                    <Skeleton variant="text" width="50%" height={14} />
                    <Skeleton variant="text" width="80%" height={12} />
                    <Skeleton variant="text" width="25%" height={10} />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <EmptyState
              variant="section"
              icon={<BellEmptyIcon />}
              title="No notifications"
              description="You're all caught up!"
            />
          ) : (
            <div className="space-y-4">
              {grouped.map(({ group, items }) => (
                <div key={group}>
                  <h3 className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2 px-1">
                    {group}
                  </h3>
                  <div className="space-y-2">
                    {items.map((n) => (
                      <SwipeableNotificationCard
                        key={n.id}
                        notification={n}
                        onClick={() => handleClick(n)}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Load more */}
              {hasMore && (
                <div className="flex justify-center py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadMore}
                    loading={isLoading}
                    className="text-brand-600 dark:text-brand-400"
                  >
                    Load older notifications
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — View All link. No hairline border (design-system
            "No Borders" rule); uses a subtle top gradient instead. */}
        {notifications.length > 0 && (
          <div className="shrink-0 px-4 py-3 bg-gradient-to-t from-neutral-50/70 to-transparent dark:from-neutral-900/60 dark:to-transparent">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAll}
              className="w-full text-brand-600 dark:text-brand-400"
            >
              View All Notifications
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
}
